import { Component, inject, OnInit, signal } from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { ActivatedRoute, Router, RouterModule } from "@angular/router";
import { Provider } from "@supabase/supabase-js";
import { TurnstileComponent } from "src/app/core/ui/turnstile/turnstile.component";
import { ClassService, InviteDetails } from "../../../features/classes/services/class.service";
import { AuthProvider, SigninResult } from "../../services/auth/auth.interface";
import { AuthService } from "../../services/auth/auth.service";
import { SUBSCRIPTION_MODULE_CONFIG } from "../../subscription.token";

const INVITE_TOKEN_KEY = 'classjax_invite_token';

@Component({
    selector: "app-signin",
    standalone: true,
    imports: [ReactiveFormsModule, RouterModule, TurnstileComponent],
    templateUrl: "./signin.component.html",
    styleUrl: "./signin.component.scss"
})
export class SigninPageComponent implements OnInit {
  private fb = inject(FormBuilder);
  private auth = inject(AuthService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private classService = inject(ClassService);

  private moduleConfig = inject(SUBSCRIPTION_MODULE_CONFIG);

  disableSigninWithEmail = !!this.moduleConfig.auth.disableSigninWithEmail;
  captchaEnabled = this.moduleConfig.auth.captcha.type === "turnstile";
  providers: AuthProvider[] = this.moduleConfig.auth.providers;
  busy = false;
  error?: string;
  notification?: string;

  // Invite context
  inviteToken = signal<string | null>(null);
  inviteDetails = signal<InviteDetails | null>(null);

  emailSigninForm: FormGroup = this.fb.group({
    email: [null, [Validators.required, Validators.email]],
    password: [null, Validators.required],
    captchaToken: [null, this.moduleConfig.auth.captcha.type === "turnstile" ? Validators.required : null],
  });

  signin() {
    if (this.emailSigninForm.valid) {
      this.error = this.notification = undefined;
      this.busy = true;
      const { email, password, captchaToken } = this.emailSigninForm.value;
      this.auth.signin(email, password, captchaToken)
        .subscribe({
          next: (res: SigninResult) => {
            if (res.confirmEmailRequired) {
              this.notification = "You have not yet confirmed your email address ...";
            } else {
              // If there's an invite token, redirect to join page
              const token = this.inviteToken();
              if (token) {
                localStorage.removeItem(INVITE_TOKEN_KEY);
                this.router.navigate(['/join', token]);
              } else {
                this.router.navigateByUrl(
                  this.route.snapshot.queryParamMap.get("redirect_url") ?? ""
                );
              }
            }
          },
          error: (err) => this.error = err,
        }).add(() => this.busy = false);
    }
  }

  signinWithProvider(provider: string) {
    this.busy = true;
    // Determine redirect URL after OAuth
    const token = this.inviteToken();
    let redirectTo: string | undefined;
    if (token) {
      // If there's an invite token, redirect back to join page after OAuth
      redirectTo = `${window.location.origin}/join/${token}`;
    } else {
      // Check for redirect_url query param
      const redirectUrl = this.route.snapshot.queryParamMap.get("redirect_url");
      if (redirectUrl) {
        redirectTo = `${window.location.origin}${redirectUrl}`;
      }
    }
    this.auth.signinWithProvider(provider as Provider, redirectTo).subscribe().add(() => this.busy = false);
  }

  ngOnInit(): void {
    if (this.auth.authenticated) this.router.navigateByUrl("");

    // Check for invite in query params
    const token = this.route.snapshot.queryParamMap.get('invite');
    if (token) {
      this.inviteToken.set(token);
      // Store in localStorage for post-signin flow
      localStorage.setItem(INVITE_TOKEN_KEY, token);
      // Fetch invite details
      this.loadInviteDetails(token);
    }
  }

  private async loadInviteDetails(token: string) {
    try {
      const details = await this.classService.getInviteByToken(token);
      if (details) {
        this.inviteDetails.set(details);
      }
    } catch (err) {
      console.error('Failed to load invite details:', err);
    }
  }
}
